import { useEffect } from "react";

const BASE = "https://geauxfotos.com";
const DEFAULT_IMAGE = BASE + "/fotos-real-collage.webp";
const BRAND_NAME = "Fotos on the Geaux";
const SERVICE_AREAS = [
  { "@type": "City", name: "New Orleans", containedInPlace: { "@type": "State", name: "Louisiana" } },
  { "@type": "City", name: "Metairie", containedInPlace: { "@type": "State", name: "Louisiana" } },
  { "@type": "AdministrativeArea", name: "Greater New Orleans" },
  { "@type": "AdministrativeArea", name: "Southeast Louisiana" },
];
const OFFER_CATALOG = {
  "@type": "OfferCatalog",
  name: "Fotos on the Geaux visual media services",
  itemListElement: [
    ["Event Photography", "/services/event-photography-new-orleans"],
    ["Foto Booth Experience", "/services/foto-booth-new-orleans"],
    ["Real Estate Media", "/services/real-estate-media-new-orleans"],
    ["Restaurant and Food Photography", "/services/restaurant-food-photography-new-orleans"],
    ["Airbnb and Hospitality Media", "/services/hospitality-airbnb-media-new-orleans"],
    ["Business Content Production", "/services/business-content-new-orleans"],
    ["Event Recap Video", "/services/event-video-new-orleans"],
    ["On-site Instant Photo Printing", "/services/instant-photo-printing-new-orleans"],
    ["Open Bar for Photos", "/services/open-bar-for-photos"],
  ].map(([name, path]) => ({
    "@type": "Offer",
    itemOffered: { "@type": "Service", name, url: BASE + path },
  })),
};

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const imageTypeFor = (src) => {
  if (/\.webp(?:$|\?)/i.test(src)) return "image/webp";
  if (/\.png(?:$|\?)/i.test(src)) return "image/png";
  return "image/jpeg";
};

const withoutContext = (item) => {
  if (!item || typeof item !== "object") return item;
  const { "@context": _context, ...rest } = item;
  return rest;
};

export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  imageAlt = "Fotos on the Geaux photography, video and Foto Booth experiences in New Orleans",
  robots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  schema = [],
}) {
  useEffect(() => {
    const url = path === "/" ? BASE + "/" : BASE + path;
    const imageType = imageTypeFor(image);

    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: BRAND_NAME });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: image });
    upsertMeta('meta[property="og:image:type"]', { property: "og:image:type", content: imageType });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt });
    upsertLink("canonical", url);

    document.head.querySelectorAll('script[data-static-seo="root"], script[data-static-seo="route"], #route-jsonld')
      .forEach((node) => node.remove());

    const extras = (Array.isArray(schema) ? schema : [schema]).filter(Boolean).map(withoutContext);
    const graph = [
      {
        "@type": "Organization",
        "@id": BASE + "/#organization",
        name: BRAND_NAME,
        alternateName: "Fotos on the Geaux New Orleans",
        url: BASE + "/",
        description: "Fotos on the Geaux is a New Orleans visual media company creating photography, video, business content, Foto Booth experiences and instant-photo keepsakes.",
        slogan: "You might forget the night. You won’t forget the foto.",
        brand: { "@type": "Brand", name: BRAND_NAME },
        areaServed: SERVICE_AREAS,
        knowsAbout: [
          "event photography", "photo booth rental", "Foto Booth", "event videography",
          "real estate photography", "food photography", "hospitality photography",
          "brand content", "instant photo printing"
        ],
        hasOfferCatalog: OFFER_CATALOG,
      },
      {
        "@type": "WebSite",
        "@id": BASE + "/#website",
        url: BASE + "/",
        name: BRAND_NAME,
        publisher: { "@id": BASE + "/#organization" },
        inLanguage: "en-US",
      },
      {
        "@type": "WebPage",
        "@id": url + "#webpage",
        url,
        name: title,
        description,
        isPartOf: { "@id": BASE + "/#website" },
        primaryImageOfPage: { "@type": "ImageObject", url: image },
        inLanguage: "en-US",
      },
    ];

    if (path !== "/") {
      graph.push({
        "@type": "BreadcrumbList",
        "@id": url + "#breadcrumb",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: BRAND_NAME, item: BASE + "/" },
          { "@type": "ListItem", position: 2, name: title.split("|")[0].trim(), item: url },
        ],
      });
    }

    graph.push(...extras);

    const script = document.createElement("script");
    script.id = "route-jsonld";
    script.type = "application/ld+json";
    script.text = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const routeSchema = document.getElementById("route-jsonld");
      if (routeSchema) routeSchema.remove();
    };
  }, [title, description, path, image, imageAlt, robots, schema]);

  return null;
}
